import { useRouteContext } from '@/app/layouts/TenantLayout'
import { toOrgSegment, toTenantSegment } from '@/lib/routeScope'
import { findUserTenant, getAccessibleTenants, isPlatformUser, resolveDefaultTenantForOrg } from '@/stores/auth'
import type { UserSettings } from '@/stores/auth'
import { useAuthStore, useCurrentOrganizationIdFromState, useSwitchTenant } from '@/stores/authStore'
import { isConsoleDeployment } from '@/utils/deployment'
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'

interface OrganizationItem {
	id: string
	name: string
	/** So a search for a project name also finds its organization. */
	projectNames: string[]
}

export function useOrganizations() {
	const user = useAuthStore(s => s.user)

	return useMemo<OrganizationItem[]>(() => {
		if (!user) {
			return []
		}
		const byId = new Map<string, OrganizationItem>()
		for (const org of user.organizations ?? []) {
			byId.set(org.id, { id: org.id, name: org.name, projectNames: [] })
		}
		// A membership can name an organization the organizations list leaves out.
		for (const tenant of isPlatformUser(user) ? getAccessibleTenants(user) : []) {
			const org = tenant.organization
			if (!org) {
				continue
			}
			const item = byId.get(org.id) ?? { id: org.id, name: org.name, projectNames: [] }
			byId.set(org.id, item)
			item.projectNames.push(tenant.name || tenant.id)
		}
		return Array.from(byId.values())
	}, [user])
}

/** The projects of one organization, with the same exclusions the navigation fallbacks apply. */
export function useProjects(orgId?: string | null) {
	const user = useAuthStore(s => s.user)
	const orgIdFromState = useCurrentOrganizationIdFromState()
	const scopedOrgId = orgId ?? orgIdFromState

	return useMemo(
		() =>
			isPlatformUser(user) && scopedOrgId
				? getAccessibleTenants(user)
						.filter(t => t.org_id === scopedOrgId)
						.map(t => ({ id: t.id, name: t.name || t.id }))
				: [],
		[user, scopedOrgId],
	)
}

export function useProjectSwitch() {
	const { tenantId: routeTenantId } = useRouteContext()
	const switchTenant = useSwitchTenant()
	const navigate = useNavigate()
	const user = useAuthStore(s => s.user)

	return useCallback(
		(tenantId: string) => {
			// A project already in the URL is left alone.
			if (tenantId === routeTenantId) {
				return
			}
			toast.info(`Switching to project "${findUserTenant(user, tenantId)?.name || tenantId}"`)
			switchTenant(tenantId, navigate)
		},
		[routeTenantId, switchTenant, navigate, user],
	)
}

/**
 * Saves a personal default (pinned organization or project) into user settings. Pins are
 * personal, so any platform user can pin, not just owners.
 */
export function usePinDefault(noun: 'organization' | 'project') {
	const [isPinning, setIsPinning] = useState(false)
	const canPin = useAuthStore(s => isPlatformUser(s.user)) && !isConsoleDeployment()

	const pin = useCallback(
		async (update: (current: UserSettings) => UserSettings, cleared: boolean) => {
			setIsPinning(true)
			try {
				await useAuthStore.getState().updateUserSettingsWith(update)
				toast.success(`Default ${noun} ${cleared ? 'cleared' : 'set'}`)
			} catch (error) {
				console.error(`Failed to update default ${noun}:`, error)
				toast.error(`Failed to update default ${noun}`)
			} finally {
				setIsPinning(false)
			}
		},
		[noun],
	)

	return { canPin, pin, isPinning }
}

/**
 * Lands on the org's own default project, console billing, or its inventory, in that order — same
 * destinations `OrgProjectSwitcher` picks, just without a dropdown wrapped around it.
 */
export function useOrganizationSwitch() {
	const navigate = useNavigate()
	const organizations = useOrganizations()
	const user = useAuthStore(s => s.user)
	const platformUser = user && isPlatformUser(user) ? user : null

	const handleOrganizationSwitch = useCallback(
		(orgId: string) => {
			const org = organizations.find(o => o.id === orgId)
			toast.info(`Switching to organization "${org?.name || orgId}"`)

			useAuthStore.getState().setCurrentOrganizationId(orgId)
			useAuthStore.getState().clearCurrentTenantUnlessBelongsToOrg(orgId)

			if (isConsoleDeployment()) {
				navigate({ to: '/orgs/$orgId/billing/$type', params: { orgId: toOrgSegment(orgId), type: 'plan-details' } })
				return
			}

			const targetProject = platformUser ? resolveDefaultTenantForOrg(platformUser, orgId) : null
			if (targetProject) {
				navigate({
					to: '/orgs/$orgId/projects/$tenantId',
					params: { orgId: toOrgSegment(orgId), tenantId: toTenantSegment(targetProject) },
				})
			} else {
				navigate({ to: '/orgs/$orgId/inventory', params: { orgId: toOrgSegment(orgId) } })
			}
		},
		[organizations, navigate, platformUser],
	)

	return { organizations, handleOrganizationSwitch }
}
